import AARTextField from "@/components/AARTextField";
import BaseDrawer from "@/components/BaseDrawer";
import CustomLabel from "@/components/CustomLabel";
import {
  ENGINEER,
  PROJECT_ADMIN,
  SUBCONTRACTOR,
  SUPER_ADMIN,
} from "@/constants/roles";
import { debounce } from "lodash";
import { Loader } from "@progress/kendo-react-indicators";
import { useAllUserGroups } from "@/hooks/useUserGroups";
import { useAllUsers } from "@/hooks/useUsers";
import { userSvc } from "@/services/user.service";
import { ICompany } from "@/interfaces/company.interface";
import { IProjectMembership } from "@/interfaces/projectMembership.interface";
import { useAppSelector } from "@/store/hooks/hooks";
import { RootState } from "@/store/store";
import { isAuthorize } from "@/utils/common";
import { buildFilters, ExistingFilters, buildUserGroupFilters } from "@/utils/kendo";
import {
  ComboBox,
  ComboBoxChangeEvent,
  MultiSelect,
  MultiSelectChangeEvent,
} from "@progress/kendo-react-dropdowns";
import {
  CheckboxChangeEvent,
  TextAreaChangeEvent,
  TextBoxChangeEvent,
} from "@progress/kendo-react-inputs";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

interface IProps {
  companies: ICompany[] | null | undefined;
  showCreateDrawer: boolean;
  setShowCreateDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  addNewUser: any;
  handleChange: (
    e:
      | TextBoxChangeEvent
      | TextAreaChangeEvent
      | CheckboxChangeEvent
      | ComboBoxChangeEvent
      | MultiSelectChangeEvent
  ) => void;
  onSave: () => void;
  isExisting: boolean;
  setIsExisting: React.Dispatch<React.SetStateAction<boolean>>;
  setAddNewUser: React.Dispatch<React.SetStateAction<any>>;
}

function WriteDrawer({
  companies,
  showCreateDrawer,
  setShowCreateDrawer,
  addNewUser,
  handleChange,
  onSave,
  isExisting,
  setIsExisting,
  setAddNewUser,
  ...drawerProps
}: IProps) {
  const { projects } = useSelector((state: RootState) => state.projects);
  const { selectedProject } = useAppSelector(
    (state: RootState) => state.projects
  );

  // Email existence check states
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);


  const { data: allUsersData, refetch: refetchAllUsers } = useAllUsers(
    {
      filter: ExistingFilters("user", selectedProject?.id),
    },
    {
      enabled: showCreateDrawer && isExisting,
    }
  );

  const { data: userGroupsData } = useAllUserGroups(
    {
      filter: buildUserGroupFilters(selectedProject?.id, undefined, false),
    },
    { enabled: true }
  );

  const [invalidFields, setInvalidFields] = useState<{
    [key: string]: boolean;
  }>({});

  const [filteredUserGroups, setFilteredUserGroups] = useState(
    userGroupsData?.data || []
  );

  // Debounced email check function
  const checkEmailExists = useMemo(
    () =>
      debounce(async (email: string, projectId?: string, excludeUserId?: string) => {
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          setEmailExists(false);
          setIsCheckingEmail(false);
          return;
        }

        // Cancel previous request if any
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();

        try {
          setIsCheckingEmail(true);

          // Build the filter with nested structure as expected by the backend
          const emailFilter: any = {
            logic: "and",
            filters: [
              {
                field: "email",
                operator: "eq",
                value: email.toLowerCase(),
              },
            ],
          };

          const filter: any = {
            logic: "and",
            filters: [emailFilter],
          };

          // Add projectId filter only if isExisting is true
          if (projectId) {
            filter.filters.push({
              logic: "and",
              filters: [
                {
                  field: "projects",
                  operator: "contains",
                  value: projectId,
                },
              ],
            });
          }

          const response = await userSvc.getAll(
            { filter },
            abortControllerRef.current.signal
          );

          // Check if the found user is not the current user being edited
          const existingUsers = response.data || [];
          const isDifferentUser = existingUsers.some(
            (user: any) => user.id !== excludeUserId
          );

          setEmailExists(isDifferentUser);
        } catch (error: any) {
          if (error?.name !== "AbortError") {
            console.error("Error checking email:", error);
          }
        } finally {
          setIsCheckingEmail(false);
        }
      }, 500),
    []
  );

  // Handle email change with validation
  const handleEmailChange = useCallback(
    (e: TextBoxChangeEvent) => {
      handleChange(e);
      const email = e.target.value as string;

      // Reset email exists state when typing
      setEmailExists(false);

      if (email) {
        setIsCheckingEmail(true);
        // Pass projectId only if isExisting is true, and pass current user id to exclude when editing
        checkEmailExists(
          email,
          isExisting ? selectedProject?.id : undefined,
          addNewUser?.id
        );
      } else {
        setIsCheckingEmail(false);
        checkEmailExists.cancel();
      }
    },
    [handleChange, checkEmailExists, isExisting, selectedProject?.id, addNewUser?.id]
  );

  // Cleanup on unmount or drawer close
  useEffect(() => {
    return () => {
      checkEmailExists.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [checkEmailExists]);

  // Reset email check state when drawer closes
  useEffect(() => {
    if (!showCreateDrawer) {
      setEmailExists(false);
      setIsCheckingEmail(false);
      checkEmailExists.cancel();
    }
  }, [showCreateDrawer, checkEmailExists]);

  const getCompanyValue = () => {
    return companies?.find((item: any) => item.id === addNewUser?.companyId);
  };

  const validateSave = () => {
    return Object.values(invalidFields).every((field) => !field);
  };

  useEffect(() => {
    if (!showCreateDrawer) {
      setInvalidFields({});
      setIsExisting(false);
    }
  }, [showCreateDrawer]);

  const getProjVal = () => {
    // Check if addNewUser and projects exist
    if (!addNewUser?.projects || !projects) {
      return [];
    }

    // If addNewUser.projects is an array, find all matching projects
    if (Array.isArray(addNewUser.projects)) {
      return projects.filter((proj) =>
        (addNewUser.projects as IProjectMembership[]).some(
          (p) => p.id === proj.id
        )
      );
    }

    return [];
  };

  const sortedUsers = [...(allUsersData?.data || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  // Inside the WriteDrawer component, add these state variables after other state declarations
  const [filteredUsers, setFilteredUsers] = useState<any>(sortedUsers);

  // Add this custom handler for ComboBox filtering and selection
  const handleUserComboBoxChange = (e: any) => {
    // Call the original change handler
    handleChange(e);

    // If this is from ComboBox and has a value (selection was made)
    if (e.target.name === "id" && e.target.value) {
      // Reset filter text and filtered list
      setFilteredUsers(sortedUsers);
    }
  };

  const handleUserComboBoxFilterChange = (e: any) => {
    const filter = e.filter || "";

    if (!filter) {
      // Reset to original list when filter is cleared
      setFilteredUsers(sortedUsers);
    } else {
      // Filter users based on filter text
      const filtered = sortedUsers.filter((user) =>
        user.name.toLowerCase().includes(filter?.value?.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  };

  const getUserValue = () => {
    return sortedUsers?.find((item: any) => item.id === addNewUser?.id);
  };

  const getUserTypeValue = () => {
    return (
      [
        {
          type: "Internal",
          id: "EntraId",
        },
        {
          type: "External",
          id: "Custom",
        },
      ]?.find((item: any) => item.id === addNewUser?.idProvider) || null
    );
  };

  // Inside the WriteDrawer component, add these state variables after other state declarations
  const [filteredCompanies, setFilteredCompanies] = useState<any>([]);

  // Add this custom handler for ComboBox filtering and selection
  const handleCompanyComboBoxChange = (e: any) => {
    // Call the original change handler
    handleChange(e);

    // If this is from ComboBox and has a value (selection was made)
    if (e.target.name === "id" && e.target.value) {
      // Reset filter text and filtered list
      setFilteredCompanies(companies);
    }
  };

  const handleCompanyComboBoxFilterChange = (e: any) => {
    const filter = e.filter || "";

    if (!filter) {
      // Reset to original list when filter is cleared
      setFilteredCompanies(companies);
    } else {
      // Filter users based on filter text
      const filtered = companies?.filter((user) =>
        user.name.toLowerCase().includes(filter?.value?.toLowerCase())
      );
      setFilteredCompanies(filtered);
    }
  };

  useEffect(() => {
    setFilteredCompanies(companies);
  }, [companies]);

  useEffect(() => {
    if (userGroupsData?.data) {
      const sorted = [...userGroupsData.data].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setFilteredUserGroups(sorted);
    }
  }, [userGroupsData]);

  const handleUserGroupFilterChange = (event: any) => {
    const filterValue = event.filter?.value?.toLowerCase() || "";

    if (!filterValue) {
      const sorted = [...(userGroupsData?.data || [])].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setFilteredUserGroups(sorted);
    } else {
      const filtered = (userGroupsData?.data || [])
        .filter((group: any) => group.name.toLowerCase().includes(filterValue))
        .sort((a, b) => a.name.localeCompare(b.name));
      setFilteredUserGroups(filtered);
    }
  };

  const getUserGroupsValue = () => {
    if (!addNewUser?.userGroups || !userGroupsData?.data) {
      return [];
    }
    
    // Map the selected user group IDs to full user group objects
    const selectedValues = userGroupsData.data.filter((group: any) =>
      addNewUser.userGroups.includes(group.id)
    );
    
    return selectedValues || [];
  };

  // Initialize userGroups when drawer opens with a user
  useEffect(() => {
    if (showCreateDrawer && addNewUser?.id && userGroupsData?.data && !isExisting) {
      // Find groups that contain this user (check both users and engineers fields)
      const userGroupIds = userGroupsData.data
        .filter((group: any) => {
          if (isEngineer) return group.engineers?.includes(addNewUser.id);
          return group.users?.includes(addNewUser.id);
        })
        .map((group: any) => group.id);
      
      // Set userGroups directly in addNewUser state
      setAddNewUser((prev: any) => ({
        ...prev,
        userGroups: userGroupIds,
      }));
    }
  }, [showCreateDrawer, userGroupsData?.data, isExisting]);

  const isSubContractor = addNewUser?.roles?.includes(SUBCONTRACTOR);
  const isEngineer = addNewUser?.roles?.includes(ENGINEER);
  const showUserGroups = isSubContractor || isEngineer;

  const renderDrawerContent = () => (
    <div className="grid gap-4">
      {isExisting && (
        <div>
          <CustomLabel label="Select User" required={true} />
          <ComboBox
            data={filteredUsers}
            placeholder="Select User"
            name="id"
            value={getUserValue() || ""}
            onChange={handleUserComboBoxChange}
            onFilterChange={handleUserComboBoxFilterChange}
            clearButton={true}
            filterable={true}
            textField="name"
            dataItemKey="id"
            suggest={true}
          />
        </div>
      )}
      <div>
        <div className="relative">
          <AARTextField
            name="email"
            value={addNewUser?.email}
            onChange={handleEmailChange}
            onValidate={(name, isValid) =>
              setInvalidFields((prev) => ({
                ...prev,
                [name]: !isValid || emailExists,
              }))
            }
            label="Email"
            required={true}
            placeholder="Enter Email"
            disabled={isExisting}
            regex={
              /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            }
            error={emailExists ? "Email exist, check existing mails" : "Invalid Email"}
          />
          {isCheckingEmail && (
            <div className="absolute right-2 top-8">
              <Loader size="small" type="pulsing" />
            </div>
          )}
        </div>
        {emailExists && !isCheckingEmail && (
          <span className="text-red-500 text-xs mt-1">Email exist, check existing mails</span>
        )}
      </div>
      <div>
        <AARTextField
          name="name"
          value={addNewUser?.name}
          onChange={handleChange}
          onValidate={(name, isValid) =>
            setInvalidFields((prev) => ({
              ...prev,
              [name]: !isValid,
            }))
          }
          label="User Name"
          required={true}
          placeholder="Enter Name"
          disabled={isExisting}
          regex={/[0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/}
          error="Invalid Name"
        />
      </div>

      <div>
        <CustomLabel label="User Type" required={true} />
        <ComboBox
          data={[
            {
              type: "Internal",
              id: "EntraId",
            },
            {
              type: "External",
              id: "Custom",
            },
          ]}
          placeholder="Select User Type"
          name="idProvider"
          textField="type"
          dataItemKey="id"
          filterable={true}
          value={getUserTypeValue()}
          onChange={handleChange}
          clearButton={false}
          disabled={isExisting}
        />
      </div>
      <div>
        <CustomLabel label="Company" required={false} />
        <ComboBox
          data={filteredCompanies?.sort((a: any, b: any) =>
            a.name.localeCompare(b.name)
          )}
          placeholder="Select Company"
          name="companyId"
          textField="name"
          dataItemKey="id"
          filterable={true}
          value={getCompanyValue() || null}
          onChange={handleCompanyComboBoxChange}
          onFilterChange={handleCompanyComboBoxFilterChange}
          clearButton={true}
          disabled={isExisting}
        />
      </div>
      {isAuthorize([SUPER_ADMIN]) && (
        <div>
          <CustomLabel required label="Project" />
          <MultiSelect
            name="projects"
            data={projects}
            value={getProjVal() || null}
            dataItemKey="id"
            textField="name"
            onChange={handleChange}
            required={false}
            placeholder="Select Project"
            disabled={isExisting}
            valid={true}
          />
        </div>
      )}
      <div>
        <CustomLabel label="Roles" required={true} />
        <MultiSelect
          data={
            isAuthorize([SUPER_ADMIN])
              ? [SUPER_ADMIN, PROJECT_ADMIN, ENGINEER, SUBCONTRACTOR]
              : [PROJECT_ADMIN, ENGINEER, SUBCONTRACTOR]
          }
          name="roles"
          onChange={handleChange}
          value={addNewUser?.roles || []}
          placeholder="Roles"
          disabled={isExisting}
          filterable={true}
        />
      </div>
      {showUserGroups && (
        <div>
          <CustomLabel label="User Groups" required={isSubContractor && !!addNewUser?.id} />
          <MultiSelect
            data={filteredUserGroups}
            textField="name"
            dataItemKey="id"
            name="userGroups"
            value={getUserGroupsValue()}
            onChange={handleChange}
            onFilterChange={handleUserGroupFilterChange}
            placeholder="Select User Groups"
            filterable={true}
          />
        </div>
      )}
    </div>
  );

  return (
    <>
      <BaseDrawer
        isOpen={showCreateDrawer}
        onClose={() => setShowCreateDrawer(false)}
        title={
          isExisting
            ? "Add Existing User"
            : addNewUser?.id
            ? "Edit User"
            : "Add New User"
        }
        primaryActionText={isExisting ? "Save Existing User" : "Save User"}
        onPrimaryAction={onSave}
        primaryActionDisabled={
          !validateSave() ||
          !addNewUser?.idProvider ||
          !addNewUser?.roles?.length ||
          (isSubContractor && addNewUser?.id && (!addNewUser?.userGroups || addNewUser?.userGroups?.length === 0)) ||
          emailExists ||
          isCheckingEmail
        }
        {...drawerProps}
        maxW="35vw"
      >
        {renderDrawerContent()}
      </BaseDrawer>
    </>
  );
}

export default WriteDrawer;
